import { z } from 'zod';
/** Global managed agent that owns every named-bot thread. */
export const formantBotAgentId = 'agt_formant_bot';
/** OpenAI realtime voices Understudy offers. Stored only; the thread page does not speak yet. */
export const formantBotVoices = [
    'marin',
    'cedar',
    'alloy',
    'ash',
    'ballad',
    'coral',
    'echo',
    'sage',
    'shimmer',
    'verse',
];
export const FormantBotVoiceSchema = z.enum(formantBotVoices);
/** Beam colours for the scope face. */
export const formantBotAvatarMints = ['mint', 'teal', 'iris', 'fog', 'rose'];
export const FormantBotAvatarMintSchema = z.enum(formantBotAvatarMints);
export const formantBotAvatarMintHex = {
    mint: '#9DFFC8',
    teal: '#7EE0D6',
    iris: '#B9B4F8',
    fog: '#C8DCCD',
    rose: '#FF9B9B',
};
/** Idle figure. The activity states (listening, thinking, error) stay shared. */
export const formantBotAvatarFigures = ['lissajous', 'orbit', 'rose', 'ribbon'];
export const FormantBotAvatarFigureSchema = z.enum(formantBotAvatarFigures);
/**
 * Per-bot scope look. Rendered from these fields; nothing is stored as an image.
 * Older traces (hue, cycles, …) fail this schema and fall back to a seeded look.
 */
export const FormantBotAvatarSchema = z
    .object({
    mint: FormantBotAvatarMintSchema,
    figure: FormantBotAvatarFigureSchema,
    /** Second frequency of the idle figure, 2–4. */
    ratio: z.number().int().min(2).max(4),
    /** 1 clockwise, -1 the other way. */
    spin: z.union([z.literal(1), z.literal(-1)]),
    /** Draw the dim second trace on states that have one. */
    voice: z.boolean(),
    /** Bloom strength, about 0.45–1.5. */
    bloom: z.number().min(0.4).max(1.6),
    /** Afterglow, about 0.6–1.6. */
    tau: z.number().min(0.55).max(1.7),
    /** Where the 4s loop starts, 0–1. */
    phase: z.number().min(0).max(1),
})
    .strict();
function hashSeed(seed) {
    let h = 2166136261;
    for (let i = 0; i < seed.length; i++)
        h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
    return h >>> 0;
}
function pick(items, n) {
    return items[n % items.length] ?? items[0];
}
/** Stable look for a bot that has not saved an avatar yet. */
export function formantBotAvatarFromSeed(seed) {
    const h = hashSeed(seed);
    return {
        mint: pick(formantBotAvatarMints, h),
        figure: pick(formantBotAvatarFigures, h >>> 3),
        ratio: (2 + (h % 3)),
        spin: h & 16 ? -1 : 1,
        voice: (h & 32) === 0,
        bloom: 0.55 + ((h >>> 8) % 80) / 100,
        tau: 0.7 + ((h >>> 12) % 70) / 100,
        phase: ((h >>> 16) % 100) / 100,
    };
}
/** A new random look for the Generate button. */
export function randomFormantBotAvatar() {
    return {
        mint: pick(formantBotAvatarMints, Math.floor(Math.random() * formantBotAvatarMints.length)),
        figure: pick(formantBotAvatarFigures, Math.floor(Math.random() * formantBotAvatarFigures.length)),
        ratio: (2 + Math.floor(Math.random() * 3)),
        spin: Math.random() < 0.5 ? -1 : 1,
        voice: Math.random() < 0.7,
        bloom: 0.5 + Math.random() * 0.9,
        tau: 0.65 + Math.random() * 0.8,
        phase: Math.random(),
    };
}
/** Bots whose thread has a message newer than this person's last look. */
export const FormantBotUnseenSchema = z.object({
    botIds: z.array(z.string()),
});
export const FormantBotSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    name: z.string(),
    instructions: z.string().nullable(),
    voice: FormantBotVoiceSchema.nullable(),
    avatar: FormantBotAvatarSchema.nullable(),
    /** Pinned above other bots. The collapsed rail shows only these. */
    primary: z.boolean(),
    persistentAgentId: z.string(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
const FormantBotScheduleInputSchema = z
    .object({
    cron: z.string().min(1).max(100),
    prompt: z.string().min(1).max(20_000),
    label: z.string().min(1).max(200).optional(),
})
    .strict();
export const CreateFormantBotInputSchema = z
    .object({
    name: z.string().trim().min(1).max(80),
    instructions: z.string().max(4000).nullable().optional(),
    voice: FormantBotVoiceSchema.nullable().optional(),
    avatar: FormantBotAvatarSchema.nullable().optional(),
    primary: z.boolean().optional(),
    /** Heartbeats attached after the thread starts. */
    schedules: z.array(FormantBotScheduleInputSchema).max(5).optional(),
})
    .strict();
/** `null` clears a field. Omitted fields stay as they are. */
export const UpdateFormantBotInputSchema = z
    .object({
    name: z.string().trim().min(1).max(80).optional(),
    instructions: z.string().max(4000).nullable().optional(),
    voice: FormantBotVoiceSchema.nullable().optional(),
    avatar: FormantBotAvatarSchema.nullable().optional(),
    primary: z.boolean().optional(),
})
    .strict();
/** Browser WebRTC offer. The API returns the realtime answer. */
export const OpenFormantBotVoiceInputSchema = z
    .object({
    sdp: z.string().min(1),
})
    .strict();
//# sourceMappingURL=formant-bot.js.map